import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/user.service';
import {
  Users,
  Mail,
  Phone,
  Calendar,
  AlertCircle,
  RefreshCw,
  Search,
  X,
  Shield,
  User as UserIcon,
  Edit,
  Trash2,
  CheckCircle,
  UserCog,
  ArrowLeft,
} from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const MembersList = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });
  const [selectedRoles, setSelectedRoles] = useState([]);

  useEffect(() => {
    if (user && isAdmin()) {
      loadMembers();
    }
  }, [user, isAdmin]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredMembers(members);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = members.filter(
        (member) =>
          member.firstName?.toLowerCase().includes(query) ||
          member.lastName?.toLowerCase().includes(query) ||
          member.email?.toLowerCase().includes(query) ||
          member.phoneNumber?.toLowerCase().includes(query)
      );
      setFilteredMembers(filtered);
    }
  }, [searchQuery, members]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userService.getUsers();
      // Filtrer uniquement les utilisateurs actifs (non en attente, non suspendus)
      const activeMembers = (response || []).filter(
        (member) => member.status === 'ACTIVE' && member.enabled
      );
      setMembers(activeMembers);
      setFilteredMembers(activeMembers);
    } catch (err) {
      console.error('Erreur lors du chargement des membres:', err);
      setError('Impossible de charger la liste des membres.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (roles) => {
    if (!roles || roles.length === 0) return null;

    if (roles.includes('ROLE_ADMIN')) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium">
          <Shield className="w-3 h-3" />
          Administrateur
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium">
        <UserIcon className="w-3 h-3" />
        Utilisateur
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const availableRoles = [
    { value: 'ROLE_USER', label: 'Utilisateur' },
    { value: 'ROLE_ADMIN', label: 'Administrateur' },
  ];

  const openEditModal = (member) => {
    setSelectedMember(member);
    setEditFormData({
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      email: member.email || '',
      phoneNumber: member.phoneNumber || '',
    });
    setShowEditModal(true);
    setError('');
    setSuccess('');
  };

  const openRoleModal = (member) => {
    setSelectedMember(member);
    setSelectedRoles(member.roles || []);
    setShowRoleModal(true);
    setError('');
    setSuccess('');
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowRoleModal(false);
    setSelectedMember(null);
    setEditFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    });
    setSelectedRoles([]);
    setError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setActionLoading('edit');
      await userService.updateUserById(selectedMember.id, editFormData);
      setSuccess('Utilisateur modifié avec succès !');
      closeModals();
      await loadMembers();
    } catch (err) {
      console.error('Erreur lors de la modification:', err);
      setError(err.message || 'Impossible de modifier l\'utilisateur.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (selectedRoles.length === 0) {
      setError('Veuillez sélectionner un rôle');
      return;
    }

    try {
      setActionLoading('role');
      await userService.updateUserRole(selectedMember.id, selectedRoles);
      setSuccess('Rôle mis à jour avec succès !');
      closeModals();
      await loadMembers();
    } catch (err) {
      console.error('Erreur lors de la mise à jour du rôle:', err);
      setError(err.message || 'Impossible de mettre à jour le rôle.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    try {
      setActionLoading(memberToDelete.id);
      setError('');
      setSuccess('');
      await userService.deleteUser(memberToDelete.id);
      setSuccess('Utilisateur supprimé avec succès !');
      await loadMembers();
      setShowDeleteModal(false);
      setMemberToDelete(null);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(err.message || 'Impossible de supprimer l\'utilisateur.');
    } finally {
      setActionLoading(null);
    }
  };

  const selectRole = (role) => {
    setSelectedRoles([role]); // Un seul rôle à la fois
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border border-gray-200">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Bouton retour */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span>Retour à l'administration</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Liste des Membres
              </h1>
              <p className="text-gray-600 mt-1">
                {filteredMembers.length} membre(s) actif(s)
              </p>
            </div>
            <button
              onClick={loadMembers}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un membre..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Messages d'erreur et succès */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Liste vide */}
        {!loading && filteredMembers.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery ? 'Aucun résultat' : 'Aucun membre'}
            </h3>
            <p className="text-gray-600">
              {searchQuery
                ? 'Aucun membre ne correspond à votre recherche.'
                : 'Il n\'y a pas encore de membres actifs.'}
            </p>
          </div>
        )}

        {/* Grille des membres */}
        {!loading && filteredMembers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMembers.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
              >
                {/* Avatar et Nom */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                    {member.firstName?.[0]}
                    {member.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {member.firstName} {member.lastName}
                    </h3>
                    <div className="mt-1">{getRoleBadge(member.roles)}</div>
                  </div>
                </div>

                {/* Informations de contact */}
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{member.email}</span>
                  </div>
                  {member.phoneNumber && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span>{member.phoneNumber}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-xs">
                      Inscrit le {formatDate(member.createdAt)}
                    </span>
                  </div>
                </div>

                {/* Actions (Admin uniquement) */}
                {isAdmin() && (
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => openEditModal(member)}
                      disabled={actionLoading === member.id}
                      className="flex items-center justify-center gap-1 px-2 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="hidden sm:inline">Modifier</span>
                    </button>
                    <button
                      onClick={() => openRoleModal(member)}
                      disabled={actionLoading === member.id}
                      className="flex items-center justify-center gap-1 px-2 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors text-xs font-medium"
                      title="Gérer le rôle"
                    >
                      <UserCog className="w-4 h-4" />
                      <span className="hidden sm:inline">Rôle</span>
                    </button>
                    <button
                      onClick={() => handleDeleteClick(member)}
                      disabled={actionLoading === member.id}
                      className="flex items-center justify-center gap-1 px-2 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-xs font-medium"
                      title="Supprimer"
                    >
                      {actionLoading === member.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      <span className="hidden sm:inline">Supprimer</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Modifier l'utilisateur */}
      {showEditModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Modifier l'utilisateur
            </h2>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    value={editFormData.firstName}
                    onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    value={editFormData.lastName}
                    onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={editFormData.phoneNumber}
                  onChange={(e) => setEditFormData({ ...editFormData, phoneNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={actionLoading === 'edit'}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                >
                  {actionLoading === 'edit' ? (
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    'Enregistrer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gérer le rôle */}
      {showRoleModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Gérer le rôle
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {selectedMember.firstName} {selectedMember.lastName}
            </p>

            <form onSubmit={handleRoleSubmit} className="space-y-4">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Sélectionnez le rôle *
                </label>
                {availableRoles.map((role) => (
                  <div
                    key={role.value}
                    className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedRoles.includes(role.value)
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    onClick={() => selectRole(role.value)}
                  >
                    <input
                      type="radio"
                      id={role.value}
                      name="userRole"
                      checked={selectedRoles.includes(role.value)}
                      onChange={() => selectRole(role.value)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label
                      htmlFor={role.value}
                      className="ml-3 flex-1 block text-sm font-medium text-gray-900 cursor-pointer"
                    >
                      {role.label}
                    </label>
                    {role.value === 'ROLE_ADMIN' && (
                      <Shield className="w-4 h-4 text-blue-600" />
                    )}
                    {role.value === 'ROLE_USER' && (
                      <UserIcon className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                ))}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={actionLoading === 'role'}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                >
                  {actionLoading === 'role' ? (
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    'Mettre à jour'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMemberToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Supprimer l'utilisateur"
        message={
          memberToDelete
            ? `Voulez-vous vraiment supprimer l'utilisateur ${memberToDelete.firstName} ${memberToDelete.lastName} ? Cette action est irréversible.`
            : ''
        }
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        loading={actionLoading === memberToDelete?.id}
      />
    </div>
  );
};

export default MembersList;
